// Converts a raw aggregate field value to a number for comparison.
// Handles golf score strings: "+3" → 3, "E" → 0, "-2" → -2.
// Value-less cells return null so those rows are left out of the sort (pushed
// last regardless of direction): blank / "-" placeholders, and non-numeric
// statuses like "DQ" / "DNF" / match-play results ("AS", "3 & 2"). Among the
// trailing rows the original backend order is preserved (stable sort).
const toComparableValue = (val) => {
  if (val == null) return null;
  if (typeof val === 'number') return val;
  const str = String(val).trim();
  if (str === '' || str === '-') return null;
  if (str === 'E') return 0;
  const num = Number(str.replace(/^\+/, ''));
  return isNaN(num) ? null : num;
};

// Purse and points are formatted numeric strings from the backend
// ("$1,200.00", "1,200.00" — number_to_currency / number_with_precision).
// Strip any currency symbol and thousands separators so they compare as
// numbers; otherwise they fall to digit-run string collation, which sorts
// "$1,200.00" before "$900.00". Symbol-agnostic, so it is not tied to a fixed
// symbol list. Blank / unparseable values return null so those rows sort last,
// like every other column.
const toFormattedNumber = (val) => {
  if (val == null) return null;
  const numeric = String(val).replace(/[^\d.-]/g, '');
  if (numeric === '') return null;
  const num = Number(numeric);
  return isNaN(num) ? null : num;
};

// Converts a position value for comparison. "T4" (tied for 4th) compares as 4;
// non-numeric statuses (CUT, WD, DNS, ...) compare as Infinity so they trail
// every numeric position — last when ascending, first when descending. Within
// the status group the backend's leaderboard order (best score first) is
// meaningful, so descending mirrors it (worst first) rather than leaving the
// stable ascending order in place — see the Infinity tie-break in
// sortAggregates.
const toPositionValue = (val) => {
  if (val == null) return null;
  const str = String(val).trim();
  if (str === '') return null;
  const tieMatch = str.match(/^T(\d+)$/);
  const num = Number(tieMatch ? tieMatch[1] : str);
  return isNaN(num) ? Infinity : num;
};

// Extracts a value from a past round for a given sort key.
// Key format: past_round_{roundId}_{total|thru|to_par}
const getPastRoundValue = (agg, sortKey) => {
  const match = sortKey.match(/^past_round_(.+)_(total|thru|to_par)$/);
  if (!match) return null;
  const [, roundId, field] = match;
  const round = (agg.pastRounds || []).find(r => r && String(r.id) === roundId);
  if (!round) return null;
  if (field === 'to_par') return toComparableValue(round.score);
  if (field === 'thru') return toThruSortValue(round.thru);
  return toComparableValue(round[field]);
};

// The name column always sorts players by surname — last name, then first
// name as tie-breaker — in every view mode, regardless of the manager's
// "First Last" / "Last, First" display choice (which agg.name encodes).
// A single member marks an individual row; team/pair/foursome rows (0 or 2+
// members) keep sorting by the displayed agg.name, the team-scope decision
// from the RTM-8629 review.
const getNameSortKey = (agg) => {
  const members = agg.memberNames;
  if (members?.length === 1) {
    const member = members[0];
    return `${String(member.lastName ?? '')} ${String(member.firstName ?? '')}`.trim();
  }
  return String(agg.name ?? '');
};

// Converts a "thru" column value to a numeric sort key so that:
//   tee times (chronological, h*60+m) < holes played < unknown < "F" (finished)
// Tee times are stored via strftime("%l:%M %p") — e.g. " 7:30 AM" / " 1:00 PM".
// A trailing "*" (two-tee-start marker) is stripped before matching.
//
// The backend also emits compound values (calculate_thru_display):
//   - multi-net teams/pairs join their balls with " / " ("F / 9", "12 / F");
//     the group is sorted by its LEAST-advanced ball (min), i.e. how far the
//     whole group has progressed.
//   - shotgun not-started rows read "7:30 AM, Hole 10A" — sort by the tee time.
//   - best-teams read as a hole range "4 - 7" — sort by the least-advanced hole.
//
// Sort bands (tee times occupy 0..1439):
const THRU_HOLE_BASE = 10000; // hole N → 10000 + N, after every tee time
const THRU_UNKNOWN = 99998; // unparseable value — just below finished
const THRU_FINISHED = 99999; // "F"
const toThruSortValue = (val) => {
  if (val == null) return null;
  // Strip leading/trailing whitespace and the optional two-tee-start asterisk.
  let str = String(val).trim().replace(/\*+$/, '');
  if (str === '' || str === '-') return null;

  // Multi-net teams/pairs: "F / 9" — key off the least-advanced component.
  if (str.includes(' / ')) {
    const parts = str.split(' / ').map((part) => toThruSortValue(part)).filter((v) => v !== null);
    return parts.length ? Math.min(...parts) : null;
  }

  // Shotgun not-started: "7:30 AM, Hole 10A" — drop the hole and keep the time.
  const teeWithHole = str.match(/^(.*?),\s*Hole\b/i);
  if (teeWithHole) str = teeWithHole[1].trim();

  // Best-teams hole range: "4 - 7" — key off the least-advanced hole.
  const rangeMatch = str.match(/^(\d{1,2})\s*-\s*(\d{1,2})$/);
  if (rangeMatch) return THRU_HOLE_BASE + Math.min(Number(rangeMatch[1]), Number(rangeMatch[2]));

  if (str === 'F') return THRU_FINISHED;

  // Explicit AM/PM: "7:30 AM", "1:00 PM", "7:30am", "1:00pm", "7:30AM" etc.
  const ampmMatch = str.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = parseInt(ampmMatch[2], 10);
    const isPm = ampmMatch[3].toLowerCase() === 'pm';
    if (isPm && h !== 12) h += 12;
    if (!isPm && h === 12) h = 0;
    return h * 60 + m;
  }

  // Bare time without AM/PM: golf hours 1-5 treated as PM
  const timeMatch = str.match(/^(\d{1,2}):(\d{2})$/);
  if (timeMatch) {
    const h = parseInt(timeMatch[1], 10);
    const m = parseInt(timeMatch[2], 10);
    const adjusted = h < 6 ? h + 12 : h;
    return adjusted * 60 + m;
  }

  const hole = Number(str);
  if (!isNaN(hole)) return THRU_HOLE_BASE + hole;

  return THRU_UNKNOWN;
};

// Descending is the exact mirror of ascending: rows that compare equal keep
// the incoming (backend) order when ascending and reverse it when descending,
// so a column of tied values reads the same top-to-bottom flipped. This covers
// the position status group (CUT/WD/DNS all compare as Infinity, in the
// backend's cut-section ranking), finite ties (three players on 77 for a
// round) and the value-less rows, which stay last in both directions but
// mirror among themselves.
export const sortAggregates = (aggregates, sortKey, sortDir) => {
  if (!aggregates?.length) return aggregates;

  const direction = sortDir === 'asc' ? 1 : -1;
  const indexByAgg = new Map(aggregates.map((agg, index) => [agg, index]));
  const mirrorIncomingOrder = (a, b) => direction * (indexByAgg.get(a) - indexByAgg.get(b));

  // Value-less rows (null keys) trail regardless of direction; everything else
  // orders by `compare`, ties falling back to the mirrored incoming order.
  const orderBy = (keyOf, compare) => (a, b) => {
    const aKey = keyOf(a);
    const bKey = keyOf(b);

    if (aKey === null && bKey === null) return mirrorIncomingOrder(a, b);
    if (aKey === null) return 1;
    if (bKey === null) return -1;

    const cmp = compare(aKey, bKey);
    return cmp === 0 ? mirrorIncomingOrder(a, b) : direction * cmp;
  };

  if (sortKey === 'name') {
    const nameKey = (agg) => getNameSortKey(agg) || null;
    return [...aggregates].sort(orderBy(nameKey, (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
  }

  if (sortKey === 'thru') {
    return [...aggregates].sort(orderBy((agg) => toThruSortValue(agg.thru), (a, b) => a - b));
  }

  const isPastRound = sortKey?.startsWith('past_round_');
  const getValue = (agg) => {
    if (isPastRound) return getPastRoundValue(agg, sortKey);
    if (sortKey === 'position') return toPositionValue(agg.position);
    if (sortKey === 'purse' || sortKey === 'points') return toFormattedNumber(agg[sortKey]);
    return toComparableValue(agg[sortKey]);
  };

  const compareValues = (a, b) => {
    if (a === b) return 0;
    return typeof a === 'number' && typeof b === 'number'
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  };

  return [...aggregates].sort(orderBy(getValue, compareValues));
};
